import { useState, useEffect, useCallback } from "react";

interface CookiePreferences {
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

const STORAGE_KEY = "cookie_preferences_v1";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

function updateAnalyticsConsent(analytics: boolean) {
  window.gtag?.("consent", "update", {
    analytics_storage: analytics ? "granted" : "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
}

function loadPreferences(): CookiePreferences | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CookiePreferences;
  } catch {
    return null;
  }
}

function savePreferences(prefs: CookiePreferences) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // localStorage może być niedostępny
  }
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState<CookiePreferences>({
    functional: true,
    analytics: false,
    marketing: false,
  });
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const saved = loadPreferences();
    if (saved) updateAnalyticsConsent(saved.analytics);
    if (!saved) {
      // Małe opóźnienie dla animacji wejścia
      const timer = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = useCallback(() => {
    const all: CookiePreferences = { functional: true, analytics: true, marketing: true };
    savePreferences(all);
    updateAnalyticsConsent(true);
    setExiting(true);
    setTimeout(() => setVisible(false), 400);
  }, []);

  const handleRejectAll = useCallback(() => {
    const minimal: CookiePreferences = { functional: true, analytics: false, marketing: false };
    savePreferences(minimal);
    updateAnalyticsConsent(false);
    setExiting(true);
    setTimeout(() => setVisible(false), 400);
  }, []);

  const handleSaveSettings = useCallback(() => {
    savePreferences(prefs);
    updateAnalyticsConsent(prefs.analytics);
    setShowSettings(false);
    setExiting(true);
    setTimeout(() => setVisible(false), 400);
  }, [prefs]);

  if (!visible) return null;

  return (
    <>
      {/* Overlay dla modala ustawień */}
      {showSettings && (
        <div
          className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowSettings(false);
          }}
        >
          <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl p-6 sm:p-8 shadow-2xl animate-slide-up max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-lg font-semibold text-foreground-950">
                Ustawienia plików cookie
              </h3>
              <button
                onClick={() => setShowSettings(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                aria-label="Zamknij ustawienia"
              >
                <i className="ri-close-line text-foreground-600" />
              </button>
            </div>

            <p className="text-sm text-foreground-600 leading-relaxed mb-6">
              Pliki cookie pomagają nam w prawidłowym działaniu strony, analizie ruchu oraz personalizacji treści.
              Poniżej możesz dostosować swoje preferencje.
            </p>

            {/* Functional - always on */}
            <div className="flex items-start gap-4 py-4 border-t border-background-200">
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground-800">Niezbędne (funkcjonalne)</p>
                <p className="text-xs text-foreground-500 mt-1 leading-relaxed">
                  Wymagane do prawidłowego działania strony. Zapewniają podstawowe funkcje, takie jak nawigacja i dostęp do bezpiecznych obszarów.
                </p>
              </div>
              <div className="w-10 h-6 flex items-center shrink-0 mt-0.5">
                <div className="w-10 h-6 rounded-full bg-accent-500/30 relative cursor-not-allowed">
                  <div className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-white shadow-sm" />
                </div>
              </div>
            </div>

            {/* Analytics */}
            <div className="flex items-start gap-4 py-4 border-t border-background-200">
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground-800">Analityczne</p>
                <p className="text-xs text-foreground-500 mt-1 leading-relaxed">
                  Pomagają nam zrozumieć, w jaki sposób odwiedzający korzystają ze strony. Dzięki nim możemy ulepszać jej działanie i treści.
                </p>
              </div>
              <button
                onClick={() => setPrefs((p) => ({ ...p, analytics: !p.analytics }))}
                className={`w-10 h-6 rounded-full transition-colors shrink-0 mt-0.5 cursor-pointer relative ${
                  prefs.analytics ? "bg-accent-500" : "bg-background-300"
                }`}
                aria-label={prefs.analytics ? "Wyłącz analityczne" : "Włącz analityczne"}
                role="switch"
                aria-checked={prefs.analytics}
              >
                <div
                  className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                    prefs.analytics ? "right-0.5" : "left-0.5"
                  }`}
                />
              </button>
            </div>

            {/* Marketing */}
            <div className="flex items-start gap-4 py-4 border-t border-background-200">
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground-800">Marketingowe</p>
                <p className="text-xs text-foreground-500 mt-1 leading-relaxed">
                  Służą do wyświetlania spersonalizowanych treści promocyjnych dopasowanych do Twoich zainteresowań.
                </p>
              </div>
              <button
                onClick={() => setPrefs((p) => ({ ...p, marketing: !p.marketing }))}
                className={`w-10 h-6 rounded-full transition-colors shrink-0 mt-0.5 cursor-pointer relative ${
                  prefs.marketing ? "bg-accent-500" : "bg-background-300"
                }`}
                aria-label={prefs.marketing ? "Wyłącz marketingowe" : "Włącz marketingowe"}
                role="switch"
                aria-checked={prefs.marketing}
              >
                <div
                  className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                    prefs.marketing ? "right-0.5" : "left-0.5"
                  }`}
                />
              </button>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <button
                onClick={handleSaveSettings}
                className="w-full px-6 py-3 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-600 transition-colors shadow-md whitespace-nowrap cursor-pointer text-sm"
              >
                Zapisz preferencje
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Główny banner */}
      <div
        className={`fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md z-[99] transition-all duration-400 ${
          exiting ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0 animate-slide-up"
        }`}
      >
        <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-xl border border-background-200/70">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-accent-100 shrink-0">
              <i className="ri-shield-check-line text-accent-500 text-base" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground-800 mb-1">
                Ta strona używa plików cookie
              </p>
              <p className="text-xs text-foreground-600 leading-relaxed">
                Korzystamy z plików cookie, aby zapewnić prawidłowe działanie strony, analizować ruch oraz personalizować treści.
                Więcej informacji znajdziesz w naszej{" "}
                <a href="/polityka-prywatnosci" className="text-accent-500 underline hover:text-accent-600 transition-colors">
                  Polityce prywatności
                </a>
                .
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => setShowSettings(true)}
              className="w-full px-4 py-2.5 text-sm font-medium text-foreground-700 bg-background-100 rounded-full hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
            >
              Ustawienia
            </button>
            <button
              onClick={handleRejectAll}
              className="w-full px-4 py-2.5 text-sm font-medium text-foreground-600 bg-background-100 rounded-full hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
            >
              Odrzuć
            </button>
            <button
              onClick={handleAcceptAll}
              className="w-full px-4 py-2.5 text-sm font-semibold text-white bg-primary-500 rounded-full hover:bg-primary-600 transition-colors shadow-sm whitespace-nowrap cursor-pointer"
            >
              Akceptuj wszystkie
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
